// src/utils/places.js
import { Loader } from "@googlemaps/js-api-loader";
import { GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_LIBRARIES } from "../config/googleMaps";

/**
 * Single loader instance + single in-flight promise
 * so multiple callers don't race.
 */
let _loader = null;
let _loaded = null;

async function ensurePlacesLoaded() {
  if (!_loaded) {
    _loader =
      _loader ||
      new Loader({
        id: GOOGLE_MAPS_SCRIPT_ID,
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
        version: "weekly",
        libraries: GOOGLE_MAPS_LIBRARIES,
      });
    _loaded = _loader.load();
  }
  await _loaded;
  return window.google; // guaranteed defined after load()
}

/**
 * Attach a Google Places Autocomplete to a plain <input>.
 * @param {HTMLInputElement} inputEl
 * @param {(place: google.maps.places.PlaceResult)=>void} onPlace
 * @returns {Promise<google.maps.places.Autocomplete|null>}
 */
export async function attachAutocomplete(inputEl, onPlace) {
  if (!inputEl) return null;

  const google = await ensurePlacesLoaded();

  // Defensive in case libraries didn’t include "places"
  const Places = google?.maps?.places;
  if (!Places?.Autocomplete) {
    console.warn("[places] google.maps.places.Autocomplete not available");
    return null;
  }

  const ac = new Places.Autocomplete(inputEl, {
    fields: ["formatted_address", "geometry", "name"],
    // If you want to lock to US results, uncomment:
    // componentRestrictions: { country: ["us"] },
  });

  ac.addListener("place_changed", () => {
    const place = ac.getPlace();
    if (onPlace) onPlace(place);
  });

  return ac;
}

